import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, MessageCircle, Send, Edit2, Trash2, Save, X } from 'lucide-react';
import { ChatBubble } from '../ui/chat-bubble';
import { supabase } from '../../lib/supabase';
import { Candidate, Evaluation, EvaluationComment } from '../../lib/supabase';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { collectRecipientsForTopic, recipientsForNewTopic } from '../../lib/notificationRecipients';

// Send notification to evaluator when candidate replies (fire-and-forget, log failures)
async function sendReplyNotification(candidateId: string, actorName: string, recipientEmail: string, commentContent?: string) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    fetch('/api/send-notification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify({ event_type: 'reply', candidate_id: candidateId, actor_name: actorName, recipient_email: recipientEmail, comment_content: commentContent }),
      keepalive: true,
    })
      .then(async (r) => {
        if (!r.ok) {
          const text = await r.text().catch(() => '');
          console.warn(`[notify] reply failed: HTTP ${r.status} ${text}`);
        }
      })
      .catch((err) => console.warn('[notify] reply network error:', err));
  } catch (err) {
    console.warn('[notify] reply setup error:', err);
  }
}

interface CandidateCommentsProps {
  evaluation: Evaluation;
  candidate: Candidate;
  onBack: () => void;
}

export default function CandidateComments({
  evaluation,
  candidate,
  onBack
}: CandidateCommentsProps) {
  const [comments, setComments] = useState<EvaluationComment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState('');
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [collapsedTopics, setCollapsedTopics] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [evaluatorInfo, setEvaluatorInfo] = useState<{name: string, email?: string, avatar_url?: string, university?: string, is_reviewer?: boolean} | null>(null);
  const [allEvaluations, setAllEvaluations] = useState<any[]>([]);
  const [evaluatorById, setEvaluatorById] = useState<Record<string, {name: string; avatar_url?: string; university?: string; is_reviewer?: boolean}>>({});
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    cancelledRef.current = false;
    loadData();
    // Mark notifications as read
    (async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (cancelledRef.current) return;
        if (user?.email) {
          await supabase
            .from('notification_queue')
            .update({ read_at: new Date().toISOString() })
            .eq('recipient_email', user.email)
            .is('read_at', null);
        }
      } catch {}
    })();
    return () => { cancelledRef.current = true; };
  }, [evaluation.id]);

  // Auto-refresh comments every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      if (!commentLoading) loadComments();
    }, 30000);
    return () => clearInterval(interval);
  }, [commentLoading]);

  const loadData = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (isCancelled()) return;
      if (user) {
        currentUserIdRef.current = user.id;
        setCurrentUserId(user.id);
      }

      // Load evaluator info for this evaluation (for notifications)
      const { data: evaluatorData } = await supabase
        .from('evaluators')
        .select('name, email, avatar_url, university, is_reviewer')
        .eq('id', evaluation.evaluator_id)
        .maybeSingle();

      if (isCancelled()) return;
      if (evaluatorData) {
        setEvaluatorInfo(evaluatorData);
      }

      // Load ALL evaluations for this candidate to get all evaluator names
      const { data: allEvalsData } = await supabase
        .from('evaluations')
        .select('id, evaluator_id')
        .eq('candidate_id', evaluation.candidate_id);

      if (isCancelled()) return;
      if (allEvalsData) {
        setAllEvaluations(allEvalsData);
        const evaluatorIds = Array.from(new Set(allEvalsData.map((e: any) => e.evaluator_id).filter(Boolean)));
        if (evaluatorIds.length) {
          const { data: infos } = await supabase.from('evaluators').select('id, name, avatar_url, university, is_reviewer').in('id', evaluatorIds);
          if (isCancelled()) return;
          const map: Record<string, any> = {};
          (infos || []).forEach((ev: any) => { map[ev.id] = { name: ev.name, avatar_url: ev.avatar_url || undefined, university: ev.university || undefined, is_reviewer: !!ev.is_reviewer }; });
          setEvaluatorById(map);
        }
      }

      // Load comments
      await loadComments();
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    } finally {
      if (!isCancelled()) setLoading(false);
    }
  };

  const loadComments = async () => {
    const isCancelled = () => cancelledRef.current;
    try {
      const { data, error } = await supabase
        .from('evaluation_comments')
        .select('*')
        .eq('evaluation_id', evaluation.id)
        .order('created_at', { ascending: true });

      if (isCancelled()) return;
      if (error) throw error;
      setComments(data || []);
    } catch (err: any) {
      if (!isCancelled()) setError(err.message);
    }
  };

  const handleSubmitComment = async () => {
    if ((!newComment.trim() && !newTopic.trim())) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      const topic = newTopic.trim() || 'general';
      const isExistingTopic = comments.some((c) => (c.topic ?? 'general') === topic);

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluation.id,
          user_id: user.id,
          user_type: 'candidate',
          author_name: candidate.name,
          author_email: candidate.email,
          content: newComment.trim() || `新しいトピック: ${newTopic.trim()}`,
          parent_comment_id: null,
          topic
        });

      if (error) throw error;

      const commentText = newComment.trim() || `新しいトピック: ${newTopic.trim()}`;
      const recipients = isExistingTopic
        ? collectRecipientsForTopic(comments, topic, null, candidate.email)
        : recipientsForNewTopic(evaluatorInfo?.email ?? null, candidate.email);
      recipients.forEach((email) => {
        sendReplyNotification(candidate.id, candidate.name, email, commentText);
      });
      setNewComment('');
      setNewTopic('');
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleSubmitReply = async (parentId: string, topic: string) => {
    if (!replyContent.trim()) return;

    setCommentLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('ユーザーが見つかりません');

      const { error } = await supabase
        .from('evaluation_comments')
        .insert({
          evaluation_id: evaluation.id,
          user_id: user.id,
          user_type: 'candidate',
          author_name: candidate.name,
          author_email: candidate.email,
          content: replyContent.trim(),
          parent_comment_id: parentId,
          topic: topic
        });

      if (error) throw error;

      const replyRecipients = collectRecipientsForTopic(
        comments,
        topic,
        evaluatorInfo?.email ?? null,
        candidate.email
      );
      replyRecipients.forEach((email) => {
        sendReplyNotification(candidate.id, candidate.name, email, replyContent.trim());
      });
      setReplyContent('');
      setReplyingTo(null);
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleEditComment = (comment: EvaluationComment) => {
    if (comment.user_id !== currentUserId) return;
    setEditingComment(comment.id);
    setEditContent(comment.content);
  };

  const handleSaveEdit = async (commentId: string) => {
    if (!editContent.trim()) return;

    setCommentLoading(true);
    try {
      const { error } = await supabase
        .from('evaluation_comments')
        .update({
          content: editContent.trim()
        })
        .eq('id', commentId)
        .eq('user_id', currentUserId!);

      if (error) throw error;

      setEditingComment(null);
      setEditContent('');
      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingComment(null);
    setEditContent('');
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!confirm('このコメントを削除しますか？')) return;

    setCommentLoading(true);
    try {
      const { error } = await supabase
        .from('evaluation_comments')
        .delete()
        .eq('id', commentId)
        .eq('user_id', currentUserId!);

      if (error) throw error;

      await loadComments();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCommentLoading(false);
    }
  };

  const organizeComments = (comments: EvaluationComment[]) => {
    const topicGroups: { [key: string]: EvaluationComment[] } = {};
    const replies: { [key: string]: EvaluationComment[] } = {};

    comments.forEach(comment => {
      if (comment.parent_comment_id) {
        if (!replies[comment.parent_comment_id]) {
          replies[comment.parent_comment_id] = [];
        }
        replies[comment.parent_comment_id].push(comment);
      } else {
        const topic = comment.topic || 'general';
        if (!topicGroups[topic]) {
          topicGroups[topic] = [];
        }
        topicGroups[topic].push(comment);
      }
    });

    return { topicGroups, replies };
  };

  const getCommentAuthorName = (comment: EvaluationComment) => {
    if (comment.author_name) return comment.author_name;
    if (comment.user_type === 'candidate') {
      return candidate.name;
    }
    const ev = allEvaluations.find((e: any) => e.id === comment.evaluation_id);
    if (ev && evaluatorById[ev.evaluator_id]) {
      const info = evaluatorById[ev.evaluator_id];
      return info.name;
    }
    if (evaluatorInfo) return evaluatorInfo.name;
    return '参加者';
  };

  const formatCommentTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return date.toLocaleTimeString('ja-JP', { 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    } else {
      return date.toLocaleDateString('ja-JP', { 
        month: 'short', 
        day: 'numeric',
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getParentInfo = (comment: EvaluationComment) => {
    if (!comment.parent_comment_id) return { author: undefined, content: undefined };
    const parent = comments.find(c => c.id === comment.parent_comment_id);
    if (parent) return { author: getCommentAuthorName(parent), content: parent.content };
    return { author: undefined, content: undefined };
  };

  const getCommentMeta = (comment: EvaluationComment): { avatarUrl?: string; university?: string; role: 'researcher' | 'evaluator' | 'reviewer' } => {
    if (comment.user_type === 'candidate') {
      return { avatarUrl: candidate.avatar_url, university: (candidate as any).university, role: 'researcher' };
    }
    const ev = allEvaluations.find((e: any) => e.id === comment.evaluation_id);
    if (ev && evaluatorById[ev.evaluator_id]) {
      const info = evaluatorById[ev.evaluator_id];
      return { avatarUrl: info.avatar_url, university: info.university, role: info.is_reviewer ? 'reviewer' : 'evaluator' };
    }
    if (evaluatorInfo) {
      return { avatarUrl: evaluatorInfo.avatar_url, university: evaluatorInfo.university, role: evaluatorInfo.is_reviewer ? 'reviewer' : 'evaluator' };
    }
    return { role: 'evaluator' };
  };

  const renderComment = (comment: EvaluationComment, allReplies: { [key: string]: EvaluationComment[] }, _level = 0) => {
    const parentInfo = getParentInfo(comment);
    const meta = getCommentMeta(comment);
    return (
    <div key={comment.id}>
      {editingComment === comment.id ? (
        <div className="mb-3 p-3 bg-muted/30 rounded-lg border border-border">
          <Textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={3} className="resize-none text-sm" />
          <div className="flex items-center justify-end mt-2">
            <div className="flex space-x-2">
              <Button variant="ghost" size="sm" onClick={handleCancelEdit}><X className="h-3 w-3 mr-1" />キャンセル</Button>
              <Button size="sm" onClick={() => handleSaveEdit(comment.id)} disabled={!editContent.trim() || commentLoading}><Save className="h-3 w-3 mr-1" />保存</Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="relative group">
          <ChatBubble
            authorName={getCommentAuthorName(comment)}
            isOwn={comment.user_id === currentUserIdRef.current}
            timestamp={comment.created_at}
            parentAuthor={parentInfo.author}
            parentContent={parentInfo.content}
            avatarUrl={meta.avatarUrl}
            university={meta.university}
            role={meta.role}
            onReply={() => setReplyingTo(comment.id)}
          >
            {comment.content}
          </ChatBubble>
          {comment.user_id === currentUserId && (
            <div className="absolute top-1 right-1 hidden group-hover:flex space-x-1">
              <button onClick={() => handleEditComment(comment)} className="p-1 rounded hover:bg-muted"><Edit2 className="h-3 w-3 text-muted-foreground" /></button>
              <button onClick={() => handleDeleteComment(comment.id)} className="p-1 rounded hover:bg-muted"><Trash2 className="h-3 w-3 text-destructive" /></button>
            </div>
          )}
        </div>
      )}

      {/* Reply form */}
      {replyingTo === comment.id && (
        <div className="mb-3 ml-10 md:ml-12">
          <div className="space-y-3 p-3 bg-muted/30 rounded-lg border border-border">
            <Textarea placeholder="返信内容を入力..." value={replyContent} onChange={(e) => setReplyContent(e.target.value)} rows={2} className="resize-none text-sm" autoFocus />
            <div className="flex items-center justify-end">
              <div className="flex space-x-2">
                <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(null); setReplyContent(''); }}>キャンセル</Button>
                <Button size="sm" onClick={() => handleSubmitReply(comment.id, comment.topic)} disabled={!replyContent.trim() || commentLoading}>
                  {commentLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div> : <Send className="h-4 w-4 mr-1" />}
                  送信
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {allReplies[comment.id]?.map(reply => renderComment(reply, allReplies, _level + 1))}
    </div>
  );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={onBack}
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          一覧に戻る
        </Button>
      </div>

      {error && (
        <Card className="border-destructive/50 bg-destructive/10">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">{error}</p>
          </CardContent>
        </Card>
      )}

      {/* Participant Info */}
      <Card>
        <CardHeader>
          <CardTitle>コメント詳細</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">参加者</span>
              <span className="font-medium">{evaluatorInfo?.name || '匿名参加者'}</span>
            </div>
            <div className="flex items-center space-x-4">
              {evaluation.liked && (
                <Badge variant="secondary" className="text-red-500">
                  ❤️ いいね
                </Badge>
              )}
              {evaluation.want_to_participate && (
                <Badge variant="secondary" className="text-green-600">
                  🙋 参加希望
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Comments Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center">
              <MessageCircle className="h-5 w-5 mr-2" />
              コメント・質問
            </CardTitle>
            <Button variant="outline" size="sm" onClick={() => setReplyingTo('__new_topic__')}>
              <MessageCircle className="h-4 w-4 mr-1" />
              新規トピック
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* New Topic Form */}
          {replyingTo === '__new_topic__' && (
            <div className="space-y-3 p-4 rounded-lg border bg-muted/20 border-border">
              <div className="flex items-center justify-between">
                <h5 className="font-semibold text-sm">新しいトピックを作成</h5>
                <Button variant="ghost" size="sm" onClick={() => { setReplyingTo(null); setNewComment(''); setNewTopic(''); }}>
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <Input placeholder="トピック名" value={newTopic} onChange={(e) => setNewTopic(e.target.value)} autoFocus />
              <Textarea placeholder="最初のコメントを入力..." value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={2} className="resize-none" />
              <div className="flex items-center justify-end">
                <Button size="sm" onClick={() => { handleSubmitComment(); setReplyingTo(null); }} disabled={!newTopic.trim() || !newComment.trim() || commentLoading}>
                  {commentLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current mr-2"></div> : <Send className="h-4 w-4 mr-1" />}
                  作成
                </Button>
              </div>
            </div>
          )}

          {/* Topic-based Comments */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <p className="text-muted-foreground text-center py-4">まだコメントがありません</p>
            ) : (
              (() => {
                const { topicGroups, replies } = organizeComments(comments);
                return Object.entries(topicGroups).map(([topic, topicComments]) => {
                  const topicKey = topic === 'general' ? '' : topic;
                  return (
                  <div key={topic} className="rounded-lg shadow-sm overflow-hidden border border-border bg-card">
                    <div
                      className="px-4 py-3 border-b flex items-center justify-between cursor-pointer bg-muted/30 border-border"
                      onClick={() => setCollapsedTopics(prev => ({ ...prev, [topic]: !prev[topic] }))}
                    >
                      <h4 className="font-semibold text-sm text-foreground flex items-center">
                        <MessageCircle className="h-4 w-4 text-primary mr-2" />
                        {topic === 'general' ? '一般' : topic}
                        <span className="ml-2 text-xs text-muted-foreground font-normal">{topicComments.length}件</span>
                      </h4>
                      <span className="text-xs text-muted-foreground">{collapsedTopics[topic] ? '展開' : '折りたたむ'}</span>
                    </div>
                    {!collapsedTopics[topic] && (
                      <>
                        <div className="p-4 space-y-1">
                          {topicComments.map(comment => renderComment(comment, replies, 0))}
                        </div>
                        <div className="px-4 py-3 border-t border-border bg-muted/10">
                          <div className="flex gap-2">
                            <Input
                              placeholder="コメントを入力..."
                              value={newTopic === topicKey ? newComment : ''}
                              onFocus={() => { setNewTopic(topicKey); }}
                              onChange={(e) => { setNewTopic(topicKey); setNewComment(e.target.value); }}
                              className="text-sm flex-1"
                            />
                            <Button size="sm" onClick={() => { setNewTopic(topicKey); handleSubmitComment(); }} disabled={!(newTopic === topicKey && newComment.trim()) || commentLoading}>
                              <Send className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                );});
              })()
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
